import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, Modal, Field, TextInput, TextArea, Select, StatusBadge } from "./adminShared";
import { formatINR } from "@/lib/format";
import { Plus, Trash2, Pencil } from "lucide-react";

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export default function Services() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/admin/services").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({ title: "", format: "1:1 In-person", durationLabel: "1.5–2 hours", durationMinutes: 105, price: 10000, priceNote: "per session, inclusive of taxes", shortDescription: "", fullDescription: "", isBookable: true, status: "published", displayOrder: 0, whoIsItFor: [], whatsIncluded: [], preparationNotes: "" });

  const save = async () => {
    const payload = { ...edit };
    try {
      if (edit.service_id) await api.put(`/admin/services/${edit.service_id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this service?")) return; await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl text-earth" style={{ fontWeight: 600 }}>Services</h1>
        <button onClick={openNew} className="btn-gold text-sm" data-testid="new-service"><Plus size={16} className="mr-1" /> New</button>
      </div>
      {!items ? <p className="text-earth/50">Loading…</p> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((s) => (
            <Card key={s.service_id} className="p-5">
              <div className="flex justify-between items-start">
                <div><StatusBadge status={s.status} /><h3 className="text-lg text-earth mt-2">{s.title}</h3><p className="text-sm text-earth/60">{s.format} · {formatINR(s.price)}</p></div>
                <div className="flex gap-1">
                  <button onClick={() => setEdit({ ...s })} className="p-2 text-earth/50 hover:text-earth" data-testid={`edit-service-${s.slug}`}><Pencil size={16} /></button>
                  <button onClick={() => del(s.service_id)} className="p-2 text-earth/50 hover:text-destructive"><Trash2 size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {edit && (
        <Modal title={edit.service_id ? "Edit service" : "New service"} onClose={() => setEdit(null)} wide>
          <Field label="Title"><TextInput value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="svc-title" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Format"><TextInput value={edit.format} onChange={(e) => setEdit({ ...edit, format: e.target.value })} /></Field>
            <Field label="Duration label"><TextInput value={edit.durationLabel} onChange={(e) => setEdit({ ...edit, durationLabel: e.target.value })} /></Field>
            <Field label="Price (₹)"><TextInput type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: parseInt(e.target.value) || 0 })} /></Field>
            <Field label="Duration (minutes)"><TextInput type="number" value={edit.durationMinutes} onChange={(e) => setEdit({ ...edit, durationMinutes: parseInt(e.target.value) || 0 })} /></Field>
          </div>
          <Field label="Price note"><TextInput value={edit.priceNote} onChange={(e) => setEdit({ ...edit, priceNote: e.target.value })} /></Field>
          <Field label="Short description"><TextArea rows={2} value={edit.shortDescription} onChange={(e) => setEdit({ ...edit, shortDescription: e.target.value })} /></Field>
          <Field label="Full description"><TextArea rows={4} value={edit.fullDescription} onChange={(e) => setEdit({ ...edit, fullDescription: e.target.value })} /></Field>
          <Field label="Who it's for (one per line)"><TextArea rows={3} value={toLines(edit.whoIsItFor)} onChange={(e) => setEdit({ ...edit, whoIsItFor: fromLines(e.target.value) })} /></Field>
          <Field label="What's included (one per line)"><TextArea rows={3} value={toLines(edit.whatsIncluded)} onChange={(e) => setEdit({ ...edit, whatsIncluded: fromLines(e.target.value) })} /></Field>
          <Field label="Preparation notes"><TextArea rows={2} value={edit.preparationNotes} onChange={(e) => setEdit({ ...edit, preparationNotes: e.target.value })} /></Field>
          <div className="grid sm:grid-cols-3 gap-4 items-end">
            <Field label="Bookable?"><Select value={edit.isBookable ? "yes" : "no"} onChange={(e) => setEdit({ ...edit, isBookable: e.target.value === "yes" })}><option value="yes">Yes — online booking</option><option value="no">No — enquiry only</option></Select></Field>
            <Field label="Status"><Select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}><option value="published">Published</option><option value="draft">Draft</option><option value="archived">Archived</option></Select></Field>
            <Field label="Display order"><TextInput type="number" value={edit.displayOrder} onChange={(e) => setEdit({ ...edit, displayOrder: parseInt(e.target.value) || 0 })} /></Field>
          </div>
          <button onClick={save} className="btn-gold w-full" data-testid="svc-save">Save service</button>
        </Modal>
      )}
    </div>
  );
}
